import React, { ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Pencil, Search, ChevronLeft, Plus, GitBranch, Trash2, Settings, Clock } from 'lucide-react'

import PartnerAvatar from 'components/PartnerAvatar'
import Button from 'components/Button'
import Card, { CardGrid } from 'components/Card'
import Container from 'components/Container'
import Spinner from 'components/Spinner'

import TWControls from 'components/controls/TWControls'
import useMediaQuery from 'hooks/useMediaQuery'

import Styled from './Layout.style'
import * as _ from 'styles/helpers'

export interface Props {
	className?: string
	children: any
}

export interface LayoutRightbarProps extends Props {
	isOffset?: boolean
}

export interface LayoutRightbarItemProps extends Props {
	title?: string
	icon?: React.ReactNode
	contentRight?: React.ReactNode
	isEmpty?: boolean
	hasNoMaxHeight?: boolean
	noPadding?: boolean
	noElevation?: boolean
}

export interface LayoutRightbarTopProps extends Props {
	img: string
	title: string
	link?: string
}

export interface LayoutSidebarProps extends Props {
	isOffset?: boolean
}

export interface LayoutSidebarItemProps extends Props {
	title?: string
	icon?: React.ReactNode
	contentRight?: React.ReactNode
	isEmpty?: boolean
	hasNoMaxHeight?: boolean
	noPadding?: boolean
	noElevation?: boolean
}

export interface LayoutSidebarTopProps extends Props {
	img: string
	title: string
	link?: string
}

export interface LayoutSidebarTop2Props extends Props {
	img: string
	title: string
	project: string
	link?: string
	isOnline: boolean
}

export interface LayoutLocationProps {
	className?: string
	avatar: string
	name: string
	city: string
	state: string
}

export interface LayoutPartnerProps {
	className?: string
	avatar: string
	name: string
	title: string
	company?: string
	link: string
	isOwner?: boolean
	isPortfolio?: boolean
	isOnline?: boolean
}

export interface LayoutPartnerRemoveProps {
	className?: string
	avatar: string
	name: string
	title: string
	company?: string
	link: string
	isOwner?: boolean
	isOnline?: boolean
}

export interface LayoutPartnershipProps {
	className?: string
	avatar: string
	name: string
	title: string
	company?: string
	buttonText: string
	buttonOnClick: Function
	isOwner?: boolean
	isOnline?: boolean
}
export interface LayoutPageToolbarProps {
	className?: string
	buttonText: string
	buttonOnClick: Function
	searchInputRef: any // what is ref in TS?
	searchInputValue: string
	searchInputOnChange: Function
}
export interface LayoutPageToolbarFilterProps {
	className?: string
	buttonText?: string
	buttonOnClick?: Function
	button2Text?: string
	button2OnClick?: Function
	button2Icon?: React.ReactNode
	customButtons?: React.ReactNode
	searchInputLabel?: string
	searchInputRef: any // what is ref in TS?
	searchInputValue: string
	searchInputOnChange: Function
	searchNoteInputRef?: any // what is ref in TS?
	searchNoteInputValue?: string
	searchNoteInputOnChange?: Function
	searchTypeInputRef?: any // what is ref in TS?
	searchTypeInputValue?: string
	searchTypeInputOnChange?: Function
	searchInput2Value?: string
	searchInput2OnChange?: Function
	searchList2?: any
	searchInput3Value?: string
	searchInput3OnChange?: Function
	searchList3?: any
}
export interface LayoutPopupToolbarFilterProps {
	className?: string
	buttonText?: string
	buttonOnClick?: Function
	searchInputRef: any // what is ref in TS?
	searchInputValue: string
	searchInputOnChange: Function
	searchCityInputRef?: any // what is ref in TS?
	searchCityInputValue?: string
	searchCityInputOnChange?: Function
	searchCityList?: any
	searchStateInputRef?: any // what is ref in TS?
	searchStateInputValue?: string
	searchStateInputOnChange?: Function
	searchStateList?: any
}
export interface LayoutPageToolbar2Props {
	className?: string
	button1Text: string
	button2Text: string
	button1OnClick: Function
	button2OnClick: Function
	searchInputRef: any // what is ref in TS?
	searchInputValue: string
	searchInputOnChange: Function
}
export interface LayoutPageToolbarNoIconProps {
	className?: string
	buttonText: string
	buttonOnClick: Function
	searchInputRef: any // what is ref in TS?
	searchInputValue: string
	searchInputOnChange: Function
}
export interface LayoutPageToolbarNoPlusProps {
	className?: string
	searchInputRef: any // what is ref in TS?
	searchInputValue: string
	searchInputOnChange: Function
}
export interface LayoutFirstTimeContentProps extends Props {
	title?: string
	icon?: React.ReactNode
	contentRight?: React.ReactNode
	isEmpty?: boolean
	hasNoMaxHeight?: boolean
	noPadding?: boolean
	noElevation?: boolean
}

export interface LayoutProfileUserCardProps {
	className?: string
}
export interface LayoutFirstProfileUserCardProps {
	className?: string
}
export interface LayoutFirstLocationCardProps {
	className?: string
}
export interface LayoutFirstProjectCardProps {
	className?: string
}
export interface LayoutFirstPartnerCardProps {
	className?: string
}
export interface LayoutSidebarPartnersProps {
	className?: string
}

export interface LayoutSidebarUserCardProps {
	className?: string
}

export interface LayoutSidebarAssetTopProps {
	className?: string
	assetName: React.ReactNode
	address: ReactNode
	webAddress?: ReactNode
	image: string
	originator?: React.ReactNode
	portfolioType?: React.ReactNode
	project?: React.ReactNode
}

const Layout = ({ children, className = '' }: Props) => (
	<Styled.Layout data-testid="Layout" className={className}>
		{children}
	</Styled.Layout>
)

const LayoutPage = ({ children, className = '' }: Props) => (
	<div data-testid="Layout.Page" className={`${className} Layout__Page`.trim()}>
		<Container className="Layout__Page__Container">{children}</Container>
	</div>
)

const LayoutPageHeader = ({ className = '', children }: Props) => (
	<Styled.LayoutPageHeader data-testid="Layout.PageHeader" className={className}>
		<div className="PageHeader__content">{children}</div>
	</Styled.LayoutPageHeader>
)

const LayoutPageToolbar = ({
	className = '',
	buttonText,
	buttonOnClick,
	searchInputRef,
	searchInputOnChange,
	searchInputValue,
}: LayoutPageToolbarProps) => {
	const isMobile = useMediaQuery(_.MQ.before_nav)
	const LayoutPageToolbarWrapper = ({ children }: { children: any }) => {
		if (isMobile) return <CardGrid>{children}</CardGrid>
		return children
	}

	return (
		<Styled.LayoutPageToolbar
			data-testid="Layout.PageToolbar"
			// className={[className, isMobile ? 'PageToolbar--isMobile' : ''].join(' ').trim()}
		>
			<LayoutPageToolbarWrapper>
				<TWControls.Input
					ref={searchInputRef}
					// label="Search"
					value={searchInputValue}
					startIcon={<Search className="w-4 h-4" />}
					onChange={searchInputOnChange}
				/>
				<Button variant="text" isInput onClick={buttonOnClick}>
					<Plus className="w-4 h-4" /> {buttonText}
				</Button>
			</LayoutPageToolbarWrapper>
		</Styled.LayoutPageToolbar>
	)
}

const LayoutPageToolbarFilter = ({
	className = '',
	buttonText,
	buttonOnClick,
	button2Text,
	button2OnClick,
	button2Icon,
	customButtons,
	searchInputLabel,
	searchInputRef,
	searchInputOnChange,
	searchNoteInputValue,
	searchNoteInputRef,
	searchNoteInputOnChange,
	searchTypeInputValue,
	searchTypeInputRef,
	searchTypeInputOnChange,
	searchInputValue,
	searchInput2Value,
	searchInput2OnChange,
	searchList2,
	searchInput3Value,
	searchInput3OnChange,
	searchList3,
}: LayoutPageToolbarFilterProps) => {
	const isMobile = useMediaQuery(_.MQ.before_nav)
	const LayoutPageToolbarWrapper = ({ children }: { children: any }) => {
		if (isMobile) return <CardGrid>{children}</CardGrid>
		return children
	}

	return (
		<Styled.LayoutPageToolbar
			data-testid="Layout.PageToolbar"
			// className={[className, isMobile ? 'PageToolbar--isMobile' : ''].join(' ').trim()}
		>
			<LayoutPageToolbarWrapper>
				<div className="flex w-full items-center flex-wrap gap-2">
					<div className="pr-2 w-full sm:w-auto sm:min-w-[180px] sm:max-w-[240px]">
						<TWControls.Input
							ref={searchInputRef}
							// label={searchInputLabel || 'Name:'}
							type="search"
							size="small"
							value={searchInputValue}
							startIcon={<Search className="w-4 h-4" />}
							onChange={searchInputOnChange}
						/>
					</div>
					{searchTypeInputOnChange && (
						<div className="pr-2 w-full sm:w-auto sm:min-w-[180px] sm:max-w-[240px]">
							<TWControls.Input
								ref={searchTypeInputRef}
								label="Type:"
								type="search"
								size="small"
								value={searchTypeInputValue}
								startIcon={<Search className="w-4 h-4" />}
								onChange={searchTypeInputOnChange}
							/>
						</div>
					)}{' '}
					{searchNoteInputOnChange && (
						<div className="pr-2 w-full sm:w-auto sm:min-w-[200px] sm:max-w-[350px]">
							<TWControls.Input
								ref={searchNoteInputRef}
								label="Note:"
								type="search"
								size="small"
								value={searchNoteInputValue}
								startIcon={<Search className="w-4 h-4" />}
								onChange={searchNoteInputOnChange}
							/>
						</div>
					)}{' '}
					{searchInput2OnChange && (
						<div className="pr-2 w-full sm:w-auto sm:min-w-[180px] sm:max-w-[300px]">
							<TWControls.SelectType
								className=""
								name="locationCity"
								label="City"
								value={searchInput2Value}
								onChange={searchInput2OnChange}
								options={searchList2}
								error={null}
								disabled={false}
							/>
						</div>
					)}{' '}
					{searchInput3OnChange && (
						<div className="pr-2 w-full sm:w-auto sm:min-w-[150px] sm:max-w-[200px]">
							<TWControls.SelectType
								className=""
								name="locationState"
								label="State"
								value={searchInput3Value}
								onChange={searchInput3OnChange}
								options={searchList3}
								error={null}
								disabled={false}
							/>
						</div>
					)}{' '}
					{(buttonText || button2Text || customButtons) && (
						<div className="flex-1 flex items-center justify-end gap-2">
							{customButtons}
							{button2Text && (
								<button
									type="button"
									className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 bg-white text-gray-700 text-sm font-medium border border-gray-400 transition-all duration-200 focus:outline-none hover:bg-[#e6f4ff] hover:border-[#0080fe] hover:text-[#052852]"
									onClick={button2OnClick as React.MouseEventHandler<HTMLButtonElement>}
								>
									{button2Icon} {button2Text}
								</button>
							)}
							{buttonText && (
								<button
									type="button"
									className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 bg-white text-gray-700 text-sm font-medium border border-gray-400 transition-all duration-200 focus:outline-none hover:bg-[#e6f4ff] hover:border-[#0080fe] hover:text-[#052852]"
									onClick={buttonOnClick as React.MouseEventHandler<HTMLButtonElement>}
								>
									<Plus className="w-3 h-3" /> {buttonText}
								</button>
							)}
						</div>
					)}{' '}
				</div>
			</LayoutPageToolbarWrapper>
		</Styled.LayoutPageToolbar>
	)
}

const LayoutPopupToolbarFilter = ({
	className = '',
	buttonText,
	buttonOnClick,

	searchInputRef,
	searchInputValue,
	searchInputOnChange,

	searchCityInputRef,
	searchCityInputValue,
	searchCityInputOnChange,
	searchCityList,

	searchStateInputRef,
	searchStateInputValue,
	searchStateInputOnChange,
	searchStateList,
}: LayoutPopupToolbarFilterProps) => {
	const isMobile = useMediaQuery(_.MQ.before_nav)
	const LayoutPopupToolbarWrapper = ({ children }: { children: any }) => {
		if (isMobile) return <CardGrid>{children}</CardGrid>
		return children
	}

	return (
		<Styled.LayoutPopupToolbar
			data-testid="Layout.PopupToolbar"
			// className={[className, isMobile ? 'PopupToolbar--isMobile' : ''].join(' ').trim()}
		>
			<LayoutPopupToolbarWrapper>
				<div className="flex flex-wrap gap-2 w-full">
					<div className="w-full sm:w-auto sm:min-w-[180px] sm:max-w-[240px]">
						<TWControls.Input
							ref={searchInputRef}
							label="Name:"
							type="search"
							size="small"
							value={searchInputValue}
							startIcon={<Search className="w-4 h-4" />}
							onChange={searchInputOnChange}
						/>
					</div>
					{searchCityInputOnChange && (
						<div className="w-full sm:w-auto sm:min-w-[180px] sm:max-w-[240px]">
							<TWControls.Input
								ref={searchCityInputRef}
								label="City:"
								type="search"
								size="small"
								value={searchCityInputValue}
								startIcon={<Search className="w-4 h-4" />}
								onChange={searchCityInputOnChange}
							/>
						</div>
					)}{' '}
					{searchStateInputOnChange && (
						<div className="w-full sm:w-auto sm:min-w-[200px] sm:max-w-[350px]">
							<TWControls.Input
								ref={searchStateInputRef}
								label="State:"
								type="search"
								size="small"
								value={searchStateInputValue}
								startIcon={<Search className="w-4 h-4" />}
								onChange={searchStateInputOnChange}
							/>
						</div>
					)}
				</div>
			</LayoutPopupToolbarWrapper>
		</Styled.LayoutPopupToolbar>
	)
}

const LayoutPageToolbar2 = ({
	className = '',
	button1Text,
	button2Text,
	button1OnClick,
	button2OnClick,
	searchInputRef,
	searchInputOnChange,
	searchInputValue,
}: LayoutPageToolbar2Props) => {
	const isMobile = useMediaQuery(_.MQ.before_nav)
	const LayoutPageToolbarWrapper = ({ children }: { children: any }) => {
		if (isMobile) return <CardGrid>{children}</CardGrid>
		return children
	}

	return (
		<Styled.LayoutPageToolbar
			data-testid="Layout.PageToolbar"
			// className={[className, isMobile ? 'PageToolbar--isMobile' : ''].join(' ').trim()}
		>
			<LayoutPageToolbarWrapper>
				<TWControls.Input
					ref={searchInputRef}
					// label="Search"
					type="search"
					size="small"
					value={searchInputValue}
					startIcon={<Search className="w-4 h-4" />}
					onChange={searchInputOnChange}
				/>
				<button
					type="button"
					title={button1Text}
					className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-[#0080fe] hover:bg-gray-100 transition-colors text-sm"
					onClick={() => button1OnClick()}
				>
					<Settings className="w-4 h-4" />
					{button1Text}
				</button>
				<button
					type="button"
					title={button2Text}
					className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-[#0080fe] hover:bg-gray-100 transition-colors text-sm"
					onClick={() => button2OnClick()}
				>
					<Plus className="w-4 h-4" />
					{button2Text}
				</button>
			</LayoutPageToolbarWrapper>
		</Styled.LayoutPageToolbar>
	)
}

const LayoutPageToolbarNoIcon = ({
	className = '',
	buttonText,
	buttonOnClick,
	searchInputRef,
	searchInputOnChange,
	searchInputValue,
}: LayoutPageToolbarNoIconProps) => {
	const isMobile = useMediaQuery(_.MQ.before_nav)
	const LayoutPageToolbarWrapper = ({ children }: { children: any }) => {
		if (isMobile) return <CardGrid>{children}</CardGrid>
		return children
	}

	return (
		<Styled.LayoutPageToolbar
			data-testid="Layout.PageToolbar"
			className={[className, isMobile ? 'PageToolbar--isMobile' : ''].join(' ').trim()}
		>
			<LayoutPageToolbarWrapper>
				<TWControls.Input
					ref={searchInputRef}
					// label="Search"
					type="search"
					size="small"
					value={searchInputValue}
					startIcon={<Search className="w-4 h-4" />}
					onChange={searchInputOnChange}
				/>
				<Button variant="text" isInput onClick={buttonOnClick}>
					{buttonText}
				</Button>
			</LayoutPageToolbarWrapper>
		</Styled.LayoutPageToolbar>
	)
}

const LayoutSecondaryNav = ({
	className = '',
	links,
	backLink,
	sourceId,
	sourceName,
}: {
	className?: string
	links: Partial<{ linkText: string; linkTo: string }>[]
	backLink?: {
		link: string
		title: string
	}
	sourceId?: string
	sourceName?: string
}) => {
	const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
	const isDesktop = useMediaQuery(_.MQ.nav)
	const isMobile = !isDesktop
	const currentRoute = useLocation()

	// Close mobile menu when route changes
	React.useEffect(() => {
		setMobileMenuOpen(false)
	}, [currentRoute.pathname])

	const rootSource = backLink => {
		if (!backLink) return null

		const rootWay = backLink.split('/')

		if (rootWay && rootWay.length > 0) {
			return rootWay[1]
		}
		return null
	}

	// Find the currently active link for mobile display
	const activeLink = links.find(
		link => link.linkTo && currentRoute.pathname.endsWith(link.linkTo.split('/').pop() || '')
	)
	const activeLinkText = activeLink?.linkText || links[0]?.linkText || 'Menu'

	return (
		<Styled.LayoutSecondaryNav data-testid="Layout.SecondaryNav" className={className}>
			<Container className={`SecondaryNav__Container ${isMobile ? 'is-mobile' : ''}`}>
				{backLink?.link && backLink?.title && (
					<div className="SecondaryNav__backlink">
						{rootSource(backLink?.link) === 'portfolios' ? (
							<Link to={`${backLink.link}`} className="SecondaryNav__backlink__link">
								<ChevronLeft className="w-4 h-4" /> {!isMobile && backLink?.title}
							</Link>
						) : (
							<Link to={`${backLink.link}/${sourceId || ''}`} className="SecondaryNav__backlink__link">
								<ChevronLeft className="w-4 h-4" /> {!isMobile && sourceName}
							</Link>
						)}{' '}
					</div>
				)}

				{/* Mobile hamburger menu */}
				{isMobile ? (
					<div className="SecondaryNav__mobile">
						<button
							type="button"
							className="SecondaryNav__mobile-toggle"
							onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
							aria-expanded={mobileMenuOpen ? 'true' : 'false'}
							aria-label="Toggle navigation menu"
						>
							<span className="SecondaryNav__mobile-current">{activeLinkText}</span>
							<svg
								className={`SecondaryNav__mobile-chevron ${mobileMenuOpen ? 'is-open' : ''}`}
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="currentColor"
							>
								<path
									fillRule="evenodd"
									d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
									clipRule="evenodd"
								/>
							</svg>
						</button>
						{mobileMenuOpen && (
							<div className="SecondaryNav__mobile-dropdown">
								{links.map(
									(link, index) =>
										link.linkTo &&
										link.linkText && (
											<NavLink
												key={index}
												to={link.linkTo}
												className={({ isActive }) => `SecondaryNav__mobile-link ${isActive ? 'is-active' : ''}`}
												onClick={() => setMobileMenuOpen(false)}
											>
												{link.linkText}
											</NavLink>
										)
								)}
							</div>
						)}
					</div>
				) : (
					/* Desktop horizontal nav */
					<nav className="SecondaryNav__links">
						<div className="SecondaryNav__links__scroll-container">
							{links.map(
								(link, index) =>
									link.linkTo &&
									link.linkText && (
										<NavLink
											key={index}
											to={link.linkTo}
											className={({ isActive }) => `SecondaryNav__link ${isActive ? 'is-active' : ''}`}
										>
											{link.linkText}
										</NavLink>
									)
							)}
						</div>
					</nav>
				)}
			</Container>
		</Styled.LayoutSecondaryNav>
	)
}

const LayoutBody = ({ children, className = '' }: Props) => (
	<Styled.LayoutBody data-testid="Layout.Body" className={className}>
		{children}
	</Styled.LayoutBody>
)

const LayoutRightbar = ({ children, className = '', isOffset = false }: LayoutRightbarProps) => {
	const match = useMediaQuery(_.MQ.before_nav)
	if (match) return null

	return (
		<Styled.LayoutRightbar data-testid="Layout.Rightbar" className={className} isOffset={isOffset}>
			{children}
		</Styled.LayoutRightbar>
	)
}

const LayoutRightbarItem = ({
	children,
	className = '',
	title,
	icon,
	contentRight,
	isEmpty = false,
	hasNoMaxHeight = false,
	noPadding = false,
	noElevation = true,
}: LayoutRightbarItemProps) => (
	<Styled.LayoutRightbarItem
		data-testid="Layout.RightbarItem"
		className={className}
		isEmpty={isEmpty}
		hasNoMaxHeight={hasNoMaxHeight}
	>
		<Card
			header={title ? ({ title, secondary: contentRight, icon } as any) : ({ secondary: contentRight, icon } as any)}
			noPadding={noPadding}
			noElevation={noElevation}
			isCentered={isEmpty}
			isLandscape={isEmpty}
		>
			{children}
		</Card>
	</Styled.LayoutRightbarItem>
)

const LayoutRightbarTop = ({ children, className = '', img, title, link }: LayoutRightbarTopProps) => {
	const TitleWrapper = ({ children }: { children: any }) => {
		if (link) return <Link to={link}>{children}</Link>
		return children
	}

	return (
		<Styled.LayoutRightbarTop data-testid="Layout.RightbarTop" className={className} img={img}>
			<div className="body">
				<TitleWrapper>
					<h2 className="title">{title}</h2>
				</TitleWrapper>
				{children}
			</div>
			<div className="img" />
		</Styled.LayoutRightbarTop>
	)
}

const LayoutSidebar = ({ children, className = '', isOffset = false }: LayoutSidebarProps) => {
	const match = useMediaQuery(_.MQ.before_nav)
	if (match) return null

	return (
		<Styled.LayoutSidebar data-testid="Layout.Sidebar" className={className} isOffset={isOffset}>
			{children}
		</Styled.LayoutSidebar>
	)
}

const LayoutSidebarNews = ({ children, className = '' }: Props) => {
	const match = useMediaQuery(_.MQ.before_nav)
	if (match) return null

	return (
		<Styled.LayoutSidebarNews data-testid="Layout.LayoutSidebarNews" className={className}>
			{children}
		</Styled.LayoutSidebarNews>
	)
}

const LayoutSidebarPartners = ({ children, className = '' }: Props) => {
	const match = useMediaQuery(_.MQ.before_nav)
	if (match) return null

	return (
		<Styled.LayoutSidebarPartners data-testid="Layout.LayoutSidebarPartners" className={className}>
			{children}
		</Styled.LayoutSidebarPartners>
	)
}

const LayoutSidebarItem = ({
	children,
	className = '',
	title,
	icon,
	contentRight,
	isEmpty = false,
	hasNoMaxHeight = false,
	noPadding = false,
	noElevation = true,
}: LayoutSidebarItemProps) => (
	<Styled.LayoutSidebarItem
		data-testid="Layout.SidebarItem"
		className={className}
		isEmpty={isEmpty}
		hasNoMaxHeight={hasNoMaxHeight}
	>
		<Card
			// header={title && ({ title, secondary: contentRight, icon } as any)}
			header={title ? { title, icon, secondary: contentRight } : undefined}
			noPadding={noPadding}
			noElevation={noElevation}
			isCentered={isEmpty}
			isLandscape={isEmpty && !hasNoMaxHeight}
		>
			{children}
		</Card>
	</Styled.LayoutSidebarItem>
)

const LayoutSidebarTop = ({ children, className = '', img, title, link }: LayoutSidebarTopProps) => {
	const TitleWrapper = ({ children }: { children: any }) => {
		if (link) return <Link to={link}>{children}</Link>
		return children
	}

	return (
		<Styled.LayoutSidebarTop data-testid="Layout.SidebarTop" className={className} img={img}>
			<div className="body">
				<TitleWrapper>
					<h2 className="title">{title}</h2>
				</TitleWrapper>
				{children}
			</div>
			<div className="img" />
		</Styled.LayoutSidebarTop>
	)
}

const LayoutSidebarTop2 = ({ children, className = '', img, title, project, link }: LayoutSidebarTop2Props) => {
	const TitleWrapper = ({ children }: { children: any }) => {
		if (link) return <Link to={link}>{children}</Link>
		return children
	}

	return (
		<Styled.LayoutSidebarTop2 data-testid="Layout.SidebarTop2" className={className} img={img}>
			<div className="body">
				<>
					{title ? (
						<h2 className="title">
							{title}
							<TitleWrapper>
								<button
									type="button"
									title="Edit"
									className="inline-flex items-center justify-center w-6 h-6 rounded text-[#0080fe] hover:bg-gray-100 transition-colors"
								>
									<Pencil className="w-3.5 h-3.5" />
								</button>
							</TitleWrapper>
						</h2>
					) : null}
				</>
				{children}
				<div className="project">{project}</div>
			</div>
		</Styled.LayoutSidebarTop2>
	)
}

const LayoutLocationItem = ({ className = '', avatar, name, city, state }: LayoutLocationProps) => {
	return (
		<Styled.LayoutLocationItem data-testid="Layout.LocationItem" className={className}>
			<div className="body">
				<div className="avatar">
					<PartnerAvatar src={avatar} name={name} size="40" />
				</div>
				<div className="details">
					<div className="name">{name}</div>
					<div className="info">
						<div>{`${city}, ${state}`}</div>
					</div>
				</div>
			</div>
		</Styled.LayoutLocationItem>
	)
}

const LayoutPartnerItem = ({
	className = '',
	avatar,
	name,
	title,
	company,
	link,
	isOwner = false,
	isOnline = false,
	isPortfolio = false,
}: LayoutPartnerProps) => {
	const isMailToLink = link.startsWith('mailto')

	const LinkWrapper = ({ children }: { children: any }) => {
		if (isMailToLink) return <a href={link}>{children}</a>
		return <Link to={link}>{children}</Link>
	}

	const content = (
		<div className="body">
			<div className="avatar">
				<PartnerAvatar src={avatar} name={name} size="40" isOnline={isOnline} />
			</div>
			<div className="details">
				<div className="name">
					{title === '(Pending)' ? (
						<div className="flex items-center justify-between w-full">
							<div>{name}</div>
						</div>
					) : (
						<div>{name}</div>
					)}
				</div>
				<div className="info">
					<div>{title}</div>
					{company && <div>{company}</div>}
				</div>
				{isOwner && <div>{isPortfolio ? `(portfolio originator)` : `(project originator)`}</div>}
			</div>
		</div>
	)

	return (
		<Styled.LayoutPartnerItem data-testid="Layout.PartnerItem" className={className}>
			<LinkWrapper>{title === '(Pending)' ? <span title="Click to Re-Invite">{content}</span> : content}</LinkWrapper>
		</Styled.LayoutPartnerItem>
	)
}

const LayoutPartnerRemoveItem = ({
	className = '',
	avatar,
	name,
	title,
	company,
	link,
	isOwner = false,
	isOnline = false,
}: LayoutPartnerRemoveProps) => {
	// const isMailToLink = link.startsWith('mailto')

	// const LinkWrapper = ({ children }: { children: any }) => {
	// 	if (isMailToLink) return <a href={link}>{children}</a>
	// 	return <Link to={link}>{children}</Link>
	// }

	return (
		<Styled.LayoutPartnerRemoveItem data-testid="Layout.PartnerRemoveItem" className={className}>
			<div className="wrapper">
				<div className="body">
					<div className="avatar">
						<PartnerAvatar src={avatar} name={name} size="40" isOnline={isOnline} />
					</div>
					<div className="details">
						<div className="name">
							{name}
							{<Trash2 className="w-4 h-4" />}
						</div>
						<div className="info">
							<div>{title}</div>
							{company && <div>{company}</div>}
						</div>
						{isOwner && <div>(portfolio originator)</div>}
					</div>
				</div>
			</div>
		</Styled.LayoutPartnerRemoveItem>
	)
}

const LayoutSidebarItemLoading = ({ className = '' }: { className: string }) => (
	<Spinner className={className} containerHeight="224px" />
)

const LayoutNewsArticle = ({ children, className = '' }: Props) => (
	<Styled.LayoutNewsArticle data-testid="Layout.NewsArticle" className={className}>
		{children}
	</Styled.LayoutNewsArticle>
)

const LayoutSidebarUserCard = ({ className = '' }: LayoutSidebarUserCardProps) => {
	// @ts-ignore
	const userLogin = useSelector(state => state.userLogin)
	const { userInfo } = userLogin

	if (!userInfo) return null

	const { name, image, title, company, email } = userInfo

	return (
		<LayoutSidebarItem noPadding noElevation>
			<Styled.LayoutSidebarUserCard data-testid="Layout.SidebarUserCard" className={className}>
				<Link to="/admin/userprofile">
					<Avatar className="LayoutSidebarUserCard__avatar">
						<AvatarImage src={image} />
						<AvatarFallback />
					</Avatar>
				</Link>
				<Link to="/admin/userprofile" className="no-underline">
					<h3 className="LayoutSidebarUserCard__user-name">{name}</h3>
				</Link>
				<div>
					{title && title}
					{title && company && ', '} {company && company}
				</div>
				<div style={{ fontSize: '11px' }}>{email}</div>
			</Styled.LayoutSidebarUserCard>
		</LayoutSidebarItem>
	)
}

const LayoutSidebarAssetTop = ({
	className = '',
	assetName,
	address,
	webAddress,
	image,
	originator,
	portfolioType,
	project,
}: LayoutSidebarAssetTopProps) => {
	const hasImage = Boolean(image)

	const assetNameString = typeof assetName === 'string' ? assetName : undefined

	const displayInitial = () => {
		if (assetNameString && assetNameString.trim().length > 0) {
			return assetNameString.trim().charAt(0).toUpperCase()
		}
		return '•'
	}

	return (
		<LayoutSidebarItem noPadding noElevation>
			<Styled.LayoutSidebarAssetTop data-testid="Layout.SidebarAssetTop" className={className}>
				<div className="LayoutSidebarAssetTop__grid">
					<div className="LayoutSidebarAssetTop__image">
						{hasImage ? (
							<PartnerAvatar src={image} name={assetNameString || ''} size="88" />
						) : (
							<div className="LayoutSidebarAssetTop__image-placeholder">{displayInitial()}</div>
						)}
					</div>
					<div className="LayoutSidebarAssetTop__details-group">
						<div className="LayoutSidebarAssetTop__details">
							{assetName && <div className="LayoutSidebarAssetTop__title">{assetName}</div>}
							{address && <div className="LayoutSidebarAssetTop__address">{address}</div>}
							{webAddress && <div className="LayoutSidebarAssetTop__address">{webAddress}</div>}
							{project && <div className="LayoutSidebarAssetTop__project">{project}</div>}
							{portfolioType && <div className="LayoutSidebarAssetTop__originator">{portfolioType}</div>}
						</div>
					</div>
				</div>
				{originator && (
					<div className="LayoutSidebarAssetTop__originator LayoutSidebarAssetTop__originator--full">{originator}</div>
				)}
			</Styled.LayoutSidebarAssetTop>
		</LayoutSidebarItem>
	)
}

const LayoutPageToolbarNoPlus = ({
	className = '',
	searchInputRef,
	searchInputOnChange,
	searchInputValue,
}: LayoutPageToolbarNoPlusProps) => {
	const isMobile = useMediaQuery(_.MQ.before_nav)
	const LayoutPageToolbarNoPlusWrapper = ({ children }: { children: any }) => {
		if (isMobile) return <CardGrid>{children}</CardGrid>
		return children
	}

	return (
		<Styled.LayoutPageToolbarNoPlus
			data-testid="Layout.PageToolbarNoPlus"
			className={[className, isMobile ? 'PageToolbarNoPlus--isMobile' : ''].join(' ').trim()}
		>
			<LayoutPageToolbarNoPlusWrapper>
				<TWControls.Input
					ref={searchInputRef}
					// label="Search"
					type="search"
					size="small"
					value={searchInputValue}
					startIcon={<Search className="w-4 h-4" />}
					onChange={searchInputOnChange}
				/>
			</LayoutPageToolbarNoPlusWrapper>
		</Styled.LayoutPageToolbarNoPlus>
	)
}

const LayoutPartnershipItem = ({
	className = '',
	avatar,
	name,
	title,
	company,
	buttonText,
	buttonOnClick,
	isOwner = false,
	isOnline = false,
}: LayoutPartnershipProps) => {
	return (
		<Styled.LayoutPartnerItem data-testid="Layout.PartnerItem" className={className}>
			<div className="body">
				<div className="avatar">
					<PartnerAvatar src={avatar} name={name} size="40" isOnline={isOnline} />
				</div>
				<div className="details">
					<div className="name">
						{/* {name} */}
						<Button variant="ghost" isInput onClick={buttonOnClick}>
							{name}
							{/* {buttonText} */}
						</Button>
					</div>
					<div className="info">
						<div>{title}</div>
						{company && <div>{company}</div>}
					</div>
					{isOwner && <div>(portfolio originator)</div>}
				</div>
			</div>
		</Styled.LayoutPartnerItem>
	)
}

const LayoutFirstTimeContent = ({
	children,
	className = '',
	title,
	icon,
	contentRight,
	isEmpty = false,
	hasNoMaxHeight = false,
	noPadding = false,
	noElevation = true,
}: LayoutFirstTimeContentProps) => (
	<Styled.LayoutFirstTimeContent
		data-testid="Layout.FirstTimeContent"
		className={className}
		isEmpty={isEmpty}
		hasNoMaxHeight={hasNoMaxHeight}
	>
		<Card
			header={title && ({ title, secondary: contentRight, icon } as any)}
			noPadding={noPadding}
			noElevation={noElevation}
			isCentered={isEmpty}
			isLandscape={isEmpty}
		>
			{children}
		</Card>
	</Styled.LayoutFirstTimeContent>
)

const LayoutProfileUserCard = ({ className = '' }: LayoutProfileUserCardProps) => {
	// @ts-ignore
	const userLogin = useSelector(state => state.userLogin)
	const { userInfo } = userLogin

	if (!userInfo) return null

	const { name, image, title, company, email } = userInfo

	return (
		<LayoutFirstTimeContent noPadding noElevation>
			<Styled.LayoutProfileUserCard data-testid="Layout.ProfileUserCard" className={className}>
				<Link to="/userprofile">
					<Avatar className="LayoutProfileUserCard__avatar">
						<AvatarImage src={image} />
						<AvatarFallback />
					</Avatar>
				</Link>
				<Link to="/userprofile">
					<h3 className="LayoutProfileUserCard__user-name">{name}</h3>
				</Link>
				<div>
					{title && title}
					{title && company && ', '} {company && company}
				</div>
				<div style={{ fontSize: '11px' }}>{email}</div>
			</Styled.LayoutProfileUserCard>
		</LayoutFirstTimeContent>
	)
}

const LayoutFirstProfileUserCard = ({ className = '' }: LayoutFirstProfileUserCardProps) => {
	// @ts-ignore
	const userLogin = useSelector(state => state.userLogin)
	const { userInfo } = userLogin

	if (!userInfo) return null

	const { name, image, title, company, email } = userInfo

	return (
		<LayoutFirstTimeContent noPadding noElevation>
			<Styled.LayoutFirstProfileUserCard data-testid="Layout.FirstProfileUserCard" className={className}>
				<Link to="">
					<Avatar className="LayoutFirstProfileUserCard__avatar">
						<AvatarImage src={image} />
						<AvatarFallback />
					</Avatar>
				</Link>
				<Link to="">
					<h3 className="LayoutFirstProfileUserCard__user-name">{name}</h3>
				</Link>
				<div>
					{title && title}
					{title && company && ', '} {company && company}
				</div>
				<div style={{ fontSize: '11px' }}>{email}</div>
			</Styled.LayoutFirstProfileUserCard>
		</LayoutFirstTimeContent>
	)
}

const LayoutFirstLocationCard = ({ className = '' }: LayoutFirstLocationCardProps) => {
	// @ts-ignore
	// const userLogin = useSelector(state => state.userLogin)
	// const { userInfo } = userLogin

	const {
		entities: locations,
		ids: locationIds,
		isInitialized: locationsInitialized,
	} = useSelector((state: any) => state.locations)
	const firstLocation = locationIds.map(id => locations[id])

	if (!firstLocation[0]) return null

	const { assetName, address, city, stateProvince, image } = firstLocation[0]

	return (
		<LayoutFirstTimeContent noPadding noElevation>
			<Styled.LayoutFirstLocationCard data-testid="Layout.FirstLocationCard" className={className}>
				<Link to="">
					<Avatar className="LayoutFirstLocationCard__avatar">
						<AvatarImage src={image} />
						<AvatarFallback />
					</Avatar>
				</Link>
				<Link to="">
					<h3 className="LayoutFirstLocationCard__user-name">{assetName}</h3>
				</Link>
				<div>{address}</div>
				<div>{`${city}, ${stateProvince}`}</div>
				{/* <div style={{ fontSize: '11px' }}>{email}</div> */}
			</Styled.LayoutFirstLocationCard>
		</LayoutFirstTimeContent>
	)
}

const LayoutFirstProjectCard = ({ className = '' }: LayoutFirstProjectCardProps) => {
	// @ts-ignore
	// const userLogin = useSelector(state => state.userLogin)
	// const { userInfo } = userLogin

	const {
		entities: projects,
		ids: projectIds,
		isInitialized: projectsInitialized,
	} = useSelector((state: any) => state.projects)
	const firstProject = projectIds.map(id => projects[id])

	if (!firstProject[0]) return null

	const { projectName, projectDescription, image } = firstProject[0]

	return (
		<LayoutFirstTimeContent noPadding noElevation>
			<Styled.LayoutFirstProjectCard data-testid="Layout.FirstProjectCard" className={className}>
				<Link to="">
					<Avatar className="LayoutFirstProjectCard__avatar">
						<AvatarImage src={image} />
						<AvatarFallback />
					</Avatar>
				</Link>
				<Link to="">
					<h3 className="LayoutFirstProjectCard__user-name">{projectName}</h3>
				</Link>
				<div>{projectDescription}</div>
				{/* <div>{`${city}, ${stateProvince}`}</div> */}
				{/* <div style={{ fontSize: '11px' }}>{email}</div> */}
			</Styled.LayoutFirstProjectCard>
		</LayoutFirstTimeContent>
	)
}

const LayoutFirstPartnerCard = ({ className = '' }: LayoutFirstPartnerCardProps) => {
	// @ts-ignore
	const userLogin = useSelector(state => state.userLogin)
	const { userInfo } = userLogin

	if (!userInfo) return null

	const { name, image, title, company, email } = userInfo

	return (
		<LayoutFirstTimeContent noPadding noElevation>
			<Styled.LayoutFirstPartnerCard data-testid="Layout.FirstPartnerCard" className={className}>
				<Link to="/userprofile">
					<Avatar className="LayoutFirstPartnerCard__avatar">
						<AvatarImage src={image} />
						<AvatarFallback />
					</Avatar>
				</Link>
				<Link to="/userprofile">
					<h3 className="LayoutFirstPartnerCard__user-name">{name}</h3>
				</Link>
				<div>
					{title && title}
					{title && company && ', '} {company && company}
				</div>
				<div style={{ fontSize: '11px' }}>{email}</div>
			</Styled.LayoutFirstPartnerCard>
		</LayoutFirstTimeContent>
	)
}

Layout.PageHeader = LayoutPageHeader
Layout.PageToolbar = LayoutPageToolbar
Layout.PageToolbarFilter = LayoutPageToolbarFilter
Layout.PopupToolbarFilter = LayoutPopupToolbarFilter
Layout.PageToolbar2 = LayoutPageToolbar2
Layout.PageToolbarNoIcon = LayoutPageToolbarNoIcon
Layout.PageToolbarNoPlus = LayoutPageToolbarNoPlus
Layout.SecondaryNav = LayoutSecondaryNav
Layout.Page = LayoutPage
Layout.Body = LayoutBody
Layout.Rightbar = LayoutRightbar
Layout.RightbarItem = LayoutRightbarItem
Layout.RightbarTop = LayoutRightbarTop
Layout.Sidebar = LayoutSidebar
Layout.SidebarItem = LayoutSidebarItem
Layout.SidebarItemLoading = LayoutSidebarItemLoading
Layout.SidebarTop = LayoutSidebarTop
Layout.SidebarTop2 = LayoutSidebarTop2
Layout.SidebarAssetTop = LayoutSidebarAssetTop
Layout.SidebarUserCard = LayoutSidebarUserCard
Layout.SidebarNews = LayoutSidebarNews
Layout.SidebarPartners = LayoutSidebarPartners
Layout.LocationItem = LayoutLocationItem
Layout.PartnerItem = LayoutPartnerItem
Layout.PartnerRemoveItem = LayoutPartnerRemoveItem
Layout.PartnershipItem = LayoutPartnershipItem
Layout.NewsArticle = LayoutNewsArticle
Layout.ProfileUserCard = LayoutProfileUserCard
Layout.FirstLocationCard = LayoutFirstLocationCard
Layout.FirstPartnerCard = LayoutFirstPartnerCard
Layout.FirstProfileUserCard = LayoutFirstProfileUserCard
Layout.FirstProjectCard = LayoutFirstProjectCard

export default Layout
